import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';

const API_URL =
  process.env.REACT_APP_FULFILLMENT_API_URL ||
  '/api/photo-kiosk-backend/aws-marketplace-fulfillment';

const UserGuideLink = () => (
  <>
    <br />
    Learn how to access it in your AWS account in the{' '}
    <a
      href="https://devsecopsinc.io/products/kiosk-media-solution/user-guide#completing-deployment"
      target="_blank"
      rel="noopener noreferrer"
    >
      "Complete Deployment"
    </a>{' '}
    section of our User's Guide{' '}
    <a
      href="https://devsecopsinc.io/products/kiosk-media-solution/user-guide"
      target="_blank"
      rel="noopener noreferrer"
    >
      here
    </a>
    .
  </>
);

const breadcrumbItems = [
  { path: '/', label: 'Home' },
  { path: '/products', label: 'Products' },
  { path: '/products/photo-kiosk-backend', label: 'Photo Kiosk Backend' },
  { path: '/products/photo-kiosk-backend/fulfillment', label: 'Registration' },
];

const ConfirmationContent = ({ success, errorMsg }) => (
  <div className="products-page">
    <Breadcrumb items={breadcrumbItems} />
    <section
      className="section section--light"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        paddingBottom: '2rem',
      }}
    >
      <div className="container" style={{ maxWidth: 700 }}>
        <div
          className="page-header"
          style={{
            background: 'none',
            padding: '2.5rem 0 1.5rem 0',
            marginBottom: 0,
            textAlign: 'center',
          }}
        >
          <p className="section__subtitle" style={{ marginBottom: '0.5rem' }}>
            Photo Kiosk Backend Product Registration
          </p>
          <h1 className="page-header__title" style={{ marginBottom: '0.5rem' }}>
            {success ? 'Thank You For Registering!' : 'Error'}
          </h1>
          <p className="page-header__description" style={{ maxWidth: 600, margin: '0 auto' }}>
            {success ? 'Your product is now successfully registered on AWS Marketplace.' : errorMsg}
          </p>
        </div>
        {success && (
          <div
            style={{
              margin: '40px auto 0 auto',
              maxWidth: 700,
              textAlign: 'center',
              fontSize: '1.1rem',
            }}
          >
            Please review the{' '}
            <a
              href="/products/photo-kiosk-backend/user-guide#complete-onboarding"
              target="_blank"
              rel="noopener noreferrer"
            >
              Complete Onboarding
            </a>{' '}
            section of the{' '}
            <a
              href="/products/photo-kiosk-backend/user-guide"
              target="_blank"
              rel="noopener noreferrer"
            >
              User Guide
            </a>{' '}
            for the information on how to access your photo kiosk backend instance.
          </div>
        )}
      </div>
    </section>
  </div>
);

const PhotoKioskFulfillment = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [testSuccess, setTestSuccess] = useState(null); // null = prod mode, true/false = test mode

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) setToken(t);
    else setError('Missing registration token.');
    const testParam = searchParams.get('test_success');
    if (testParam === 'true') setTestSuccess(true);
    else if (testParam === 'false') setTestSuccess(false);
    else setTestSuccess(null);
  }, [searchParams]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (testSuccess === true) {
      setTimeout(() => {
        setSubmitted(true);
        setLoading(false);
      }, 500);
      return;
    }
    if (testSuccess === false) {
      setTimeout(() => {
        setError('Registration failed. Please try again or contact support.');
        setLoading(false);
      }, 500);
      return;
    }
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
      });
      if (!res.ok) throw new Error('Registration failed');
      setSubmitted(true);
    } catch (err) {
      setError('Registration failed. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (error) return <ConfirmationContent success={false} errorMsg={error} />;
  if (submitted) return <ConfirmationContent success={true} />;

  return (
    <div className="products-page">
      <Breadcrumb items={breadcrumbItems} />
      <section
        className="section section--light"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          paddingBottom: '2rem',
        }}
      >
        <div className="container" style={{ maxWidth: 700 }}>
          <div
            className="page-header"
            style={{
              background: 'none',
              padding: '2.5rem 0 1.5rem 0',
              marginBottom: 0,
              textAlign: 'center',
            }}
          >
            <p className="section__subtitle" style={{ marginBottom: '0.5rem' }}>
              AWS Marketplace Product Registration
            </p>
            <h1 className="page-header__title" style={{ marginBottom: '0.5rem' }}>
              Photo Kiosk Backend
            </h1>
            <p className="page-header__description" style={{ maxWidth: 600, margin: '0 auto' }}>
              Enter your email address to complete your product registration.
            </p>
          </div>
          <form onSubmit={handleSubmit} style={{ margin: '40px auto 0 auto', maxWidth: 480 }}>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label htmlFor="email" style={{ fontWeight: 500, marginBottom: 8, display: 'block' }}>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="form-control"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={loading}
                style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
              />
            </div>
            <button className="btn btn--primary" type="submit" disabled={loading || !email}>
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default PhotoKioskFulfillment;
